import React, { useState, useEffect, useMemo, useContext } from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  KeyboardAvoidingView,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  Platform,
  Modal,
  Dimensions,
  PixelRatio,
} from "react-native";
import { useNavigation, useRoute } from "@react-navigation/native";
import * as Google from "expo-auth-session/providers/google";
import AppleSignin from "react-apple-signin-auth";
import * as AuthSession from "expo-auth-session";
import { useQuery } from "@tanstack/react-query";
import { Ionicons, MaterialCommunityIcons } from "@expo/vector-icons";
import { Image } from "expo-image";

import Colours from "../../utils/Colours";
import TopHeaderComponent from "../aTopHeader/TopHeaderComponent";

import AppContext from "../../context/AppContext";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
// import axios from "../../api/axios";
import axios, { axiosWithCredentials } from "../../api/axios";

import ErrorAlertComp from "../../alertComp/ErrorAlertComp";
import { SafeAreaView } from "react-native-safe-area-context";

const { width, height } = Dimensions.get("window");

const BASE_WIDTH = 375;
const BASE_HEIGHT = 812;

const dynamicFontSize = (size) => {
  const scaleFactor = Math.min(width / BASE_WIDTH, height / BASE_HEIGHT);
  return Math.round(PixelRatio.roundToNearestPixel(size * scaleFactor));
};

const dynamicSpacing = (size) => (width / BASE_WIDTH) * size;
const dynamicSize = (size) => (width / BASE_WIDTH) * size;

const TableInvitationCheckout = () => {
  const [isErrorCompVisible, setIsErrorCompVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoadingAction, setIsLoadingAction] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState("");

  const [userInfo, setUserInfo] = useState(null);
  const { setAuth, setGoogleOnboardData } = useContext(AppContext);

  // const { isGuest, phoneNumber, username } = useAuth();
  // const { setGoogleOnboardData } = useContext(AppContext);

  const [isAuthLoading, setIsAuthLoading] = useState(false);

  const navigation = useNavigation();
  const route = useRoute();
  const { userToken } = useContext(AppContext);
  const axiosPrivate = useAxiosPrivate();
  const isLoggedIn = !!userToken;

  // const { mode, userstats } = route.params || {};

  const tableToken = route?.params?.tableToken;

  /* ----------------------------------------------------
   * FETCH DATA LAYER
   * ---------------------------------------------------- */
  const fetchTableDetailsByToken = async (token) => {
    if (!token) return null;
    const response = await axios.get(`/getVirtualTableByToken/getVirtualTableByToken/${token}`);
    return response.data.tableData;
  };

  const { data: tableDetails, isLoading: isLoadingTable } = useQuery({
    queryKey: ["verifyTable", tableToken],
    queryFn: () => fetchTableDetailsByToken(tableToken),
    enabled: !!tableToken,
    staleTime: 1000 * 60 * 60,
  });

  const targetTableId = tableDetails?._id;
  const invitedTableName = tableDetails?.tableName || "Exclusive Guest Table";
  const waiterID = tableDetails?.waiterID;
  const brandID = tableDetails?.associatedBrand?._id;
  const brandName = tableDetails?.associatedBrand?.brandName || "Premium Venue";
  const brandAddress = tableDetails?.associatedBrand?.brandAddress || "Location Details";
  const brandImage = tableDetails?.associatedBrand?.image || "https://placehold.co/150";

  const handleAppleSuccess = async (response) => {
    // Apple returns:
    // response.authorization.id_token
    // response.user (ONLY sent on the FIRST login)

    const appleCredential = {
      identityToken: response.authorization?.id_token,
      user: response.user || null,
    };

    console.log("appleCredential", appleCredential);

    // Pass it directly to your pipeline function
    await executeAppleGuestSignup(appleCredential);
  };

  async function executeAppleGuestSignup(appleCredential) {
    if (isAuthLoading) return;

    try {
      setIsAuthLoading(true);

      const { identityToken, user } = appleCredential;

      // STEP 1: Process Apple Guest Identity Authentication
      const authResponse = await axiosWithCredentials.post("/appleGuestAuth/appleGuestAuth", {
        id_token: identityToken,
        rawUser: user, // Sent by Apple SDK on initial sign-in
      });

      const strictLocalToken = authResponse.data.accessToken;

      // STEP 2: Update local authentication state for the guest user
      setAuth({
        accessToken: strictLocalToken,
        isProfileComplete: true,
      });

      // STEP 3: Close the authentication modal on success
    } catch (error) {
      console.error("Frontend Apple Auth Catch Triggered:", error);

      setErrorMessage(
        error?.response?.data?.message ||
          error.message ||
          "Apple authentication failed. Please try again.",
      );
      setIsErrorCompVisible(true);
    } finally {
      setIsAuthLoading(false);
    }
  }

  const handleAppleFailure = (error) => {
    console.error("Apple Sign-In Error:", error);
    setErrorMessage("Apple Sign-In Error");
    setIsErrorCompVisible(true);
  };

  // Safeguard: Redirect if the metadata returned from the token is corrupt or missing
  useEffect(() => {
    if (isLoadingTable) return;

    if (!brandID || !waiterID || brandID === "undefined" || waiterID === "undefined") {
      if (isLoggedIn) {
        navigation.replace("main");
      } else {
        navigation.navigate("getStarted");
      }
    }
  }, [brandID, waiterID, isLoadingTable, isLoggedIn]);

  // Clean numerical digits for internal calculations
  const cleanedPhoneDigits = useMemo(() => {
    let cleaned = whatsappNumber.replace(/\D/g, "");
    cleaned = cleaned.replace(/^0+/, "");
    if (cleaned.startsWith("234") && cleaned.length > 10) {
      cleaned = cleaned.slice(3);
    }
    return cleaned.replace(/^0+/, "");
  }, [whatsappNumber]);

  // Form Submission Validation Guard
  const canProceed = useMemo(() => {
    if (isLoggedIn) {
      return true; // Already signed in, just click to join array
    }
    return cleanedPhoneDigits.length === 10; // Must be exactly a 10-digit native line string
  }, [isLoggedIn, cleanedPhoneDigits]);

  // const handleBackNavigation = () => {
  //   const routes = navigation.getState()?.routeNames || [];
  //   const targetScreen = routes.includes("main") ? "main" : "Home";
  //   if (Platform.OS === "web" && !isLoggedIn) {
  //     navigation.navigate("getStarted");
  //   } else {
  //     navigation.navigate(targetScreen);
  //   }
  // };

  const handleBackNavigation = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      // If opened via direct link and can't go back in stack history
      if (Platform.OS === "web") {
        window.location.href = isLoggedIn ? "/" : "/app";
      } else {
        navigation.navigate(isLoggedIn ? "main" : "getStarted");
      }
    }
  };

  /* ----------------------------------------------------
   * EXECUTE JOINGUEST PIPELINE
   * ---------------------------------------------------- */
  const handleProceed2 = async () => {
    let finalPhoneNumber = null;

    if (!isLoggedIn) {
      if (cleanedPhoneDigits.length !== 10) {
        setErrorMessage("You might have entered a wrong number");
        setIsErrorCompVisible(true);
        return;
      }
      finalPhoneNumber = `234${cleanedPhoneDigits}`;
    }

    const payload = {
      phoneNumber: finalPhoneNumber,
      tableId: targetTableId,
      associatedBrand: brandID,
      waiterID,
    };

    try {
      setIsLoadingAction(true);

      if (isLoggedIn) {
        // Logged-in user: Add direct to array pipeline on backend layout controller
        const response = await axiosPrivate.post("/joinVirtualTable/joinVirtualTable", payload);
        if (response.data.success) {
          // Navigates securely to home dashboard viewport or active split-bill layout

          navigation.navigate("main", {
            screen: "home",
            params: { activeTableId: targetTableId },
          });
        }
      } else {
        // Unauthenticated User: Request an SMS/WhatsApp OTP token first
        const response = await axios.post("/requestOtp/requestOtp", payload);
        if (response.status === 200 || response.data?.success) {
          navigation.navigate("anonymousOnboardingOTP", {
            whatsappNumber: payload.phoneNumber,
            tableId: targetTableId,
            mode: "joinTableOTP",
          });
        }
      }
    } catch (error) {
      console.error("Pipeline assignment processing failed:", error);
      setErrorMessage(error?.response?.data?.message || "Something went wrong. Please try again");
      setIsErrorCompVisible(true);
    } finally {
      setIsLoadingAction(false);
    }
  };

  const handleProceed = async () => {
    // const payload = {
    //   // phoneNumber: finalPhoneNumber,
    //   tableId: targetTableId,
    //   associatedBrand: brandID,
    //   waiterID,
    // };
    try {
      setIsLoadingAction(true);

      if (isLoggedIn) {
        // Logged-in user: Add direct to array pipeline on backend layout controller
        const response = await axiosPrivate.post("/joinVirtualTable/joinVirtualTable", {
          tableId: targetTableId,
        });
        if (response.data.success) {
          // Navigates securely to home dashboard viewport or active split-bill layout

          navigation.navigate("main");

          // navigation.navigate("main", {
          //   screen: "home",
          //   params: { activeTableId: targetTableId },
          // });
        }
      }
    } catch (error) {
      console.error("Pipeline assignment processing failed:", error);
      setErrorMessage(error?.response?.data?.message || "Something went wrong. Please try again");
      setIsErrorCompVisible(true);
    } finally {
      setIsLoadingAction(false);
    }
  };

  const [request, response, promptAsync] = Google.useAuthRequest({
    // 1. For Expo Go or Web testing
    // webClientId: "607742650924-4qvotvu1q75g3a9dchind088c87pmpco.apps.googleusercontent.com",
    webClientId: "607742650924-2ei9go2bl635sl9gh1pakdj2rqr3s0kn.apps.googleusercontent.com",

    // 2. For your physical iPhone build
    iosClientId: "607742650924-mbcms1rms9i91dj5l9cdp4143lgua83c.apps.googleusercontent.com",

    // 3. For your Android build
    androidClientId: "607742650924-k779l559pl0hjfse5a2kch7cqh5j71jf.apps.googleusercontent.com",

    // redirectUri: Platform.OS === "web" ? "https://app.getmeeza.com" : undefined,
    // redirectUri: Platform.OS === "web" ? "http://localhost:8081/" : undefined,
    redirectUri: AuthSession.makeRedirectUri({
      scheme: "http",
      // host: '127.0.0.1', // Swap from localhost to loopback IP
      host: "localhost", // Swap from localhost to loopback IP
      port: 8081,
      path: "", // Explicitly forces NO trailing slash
    }),
  });

  useEffect(() => {
    if (response?.type === "success") {
      const { authentication } = response;
      const { accessToken } = authentication;

      sendGoogleTokenToBackend(accessToken);
    }
  }, [response]);

  const sendGoogleTokenToBackend = async (token) => {
    try {
      setIsAuthLoading(true);
      // Axios automatically parses incoming JSON and throws errors for non-2xx status codes
      const response = await axiosWithCredentials.post("/googleAuth/googleAuth", {
        token: token,
        tableId: targetTableId,
      });

      const data = response.data;
      console.log("RESPOSEEE GOOGLE0", response, data);

      if (data.success) {
        // setAuth({ accessToken: data.accessToken });

        if (data.tableJoinNotice) {
          sessionStorage.setItem("table_join_notice", data.tableJoinNotice);
          // Note: If this is React Native / Expo, use AsyncStorage:
          // await AsyncStorage.setItem("table_join_notice", data.tableJoinNotice);
        }

        setAuth({
          accessToken: data.accessToken,
          isProfileComplete: data.isProfileComplete,
        });

        //navigation.navigate("main");
        // window.location.href = "/";

        switch (data.action) {
          case "NAVIGATE_TO_ONBOARDING":
            console.log("setup profile - onboard");

            console.log("data.action", data.action, data.message);

            // 💾 Save the full backend response directly into global memory
            setGoogleOnboardData({
              username: data.username || "guest_",
              firstname: data.firstname || data.suggestedName?.split(" ")[0] || "",
              lastname: data.lastname || data.suggestedName?.split(" ")[1] || "",
              email: data.email || "",
              profilePicture: data.suggestedAvatar || "",
              isGoogleAuth: true,
            });

            // 🚀 Safely navigate using JUST the param your linking config expects
            navigation.navigate("user_identity", { userId: data.userId });
            break;

          case "NAVIGATE_TO_PROFILE_SETUP":
            console.log("setup profile");

            // User exists but has an active "guest_" username status framework flag
            navigation.navigate("EditProfileScreen", {
              userId: data.userId,
              isGuestUpgrade: true,
            });
            break;

          case "NAVIGATE_TO_HOME":
            console.log("home");
            // setAuth({ accessToken: data.accessToken });
            // Standard verified account profile setup — skip directly to core screens
            // Handle saving your application's JWT session token tokens here
            // navigation.navigate("main");
            // navigation.navigate("Home");
            break;

          default:
            console.warn("Unexpected routing execution action received:", data.action);
        }
      }
    } catch (error) {
      // Catch-all structural error logging block
      if (error.response) {
        // The server responded with a status code outside the 2xx range
        console.error("Backend Error Data:", error.response.data);
      } else if (error.request) {
        // The request was made but no response was received (Network issues)
        console.error("Network Connectivity Timeout:", error.request);
      } else {
        console.error("Axios Runtime Config Error:", error.message);
      }
    } finally {
      setIsAuthLoading(false);
    }
  };
  const handleAppleSignIn = async () => {};
  const handleGoogleSignIn = async () => {};

  if (isLoadingTable || isAuthLoading) {
    return (
      <View style={styles.centerLoader}>
        <ActivityIndicator size="large" color={Colours.PRIMARY} />
        <Text style={styles.loadingTextText}>Loading Invitation Details...</Text>
      </View>
    );
  }

  if (!brandID || !waiterID || brandID === "undefined" || waiterID === "undefined") {
    return null;
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <TopHeaderComponent headerText={"Table Invite"} hideScreen={handleBackNavigation} />

      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={{ flex: 1, backgroundColor: Colours.LIGHTEST }}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          {/* VENUE HERO CARD */}
          <View style={styles.brandHeroCard}>
            <Image
              source={{ uri: brandImage }}
              style={styles.brandImage}
              contentFit="cover"
              cachePolicy="disk"
            />
            <View style={styles.brandMetaInfo}>
              <Text style={styles.nameTxt} numberOfLines={1}>
                {brandName}
              </Text>
              <Text style={styles.emailTxt} numberOfLines={2}>
                {brandAddress}
              </Text>
            </View>
          </View>

          {/* DISPLAY TABLE INVITATION SECTION */}
          <View style={styles.section}>
            <Text style={styles.sectionLabel}>1. YOUR INVITATION</Text>
            <View style={styles.invitationCard}>
              <View style={styles.badgeRow}>
                <MaterialCommunityIcons name="mail-ru" size={dynamicSize(16)} color="#E0A96D" />
                <Text style={styles.invitationBadgeText}>You are invited to this table</Text>
              </View>
              <Text style={styles.styledTableTitle}>{invitedTableName}</Text>
            </View>
          </View>

          {/* SOCIAL AUTHENTICATION SECTION (Only shown for unauthenticated users) */}
          {!isLoggedIn ? (
            <View style={styles.section}>
              <Text style={styles.sectionLabel}>2. SIGN IN TO CONTINUE</Text>
              <View style={styles.authBtnContainer}>
                {/* Google Sign-In Button */}
                <TouchableOpacity
                  style={styles.googleBtn}
                  activeOpacity={0.85}
                  // onPress={handleGoogleSignIn}
                  onPress={() => promptAsync({ useProxy: true })}
                  disabled={isLoadingAction}
                >
                  <Ionicons name="logo-google" size={dynamicSize(18)} color={Colours.PRIMARY} />
                  <Text style={styles.googleBtnText}>Continue with Google</Text>
                </TouchableOpacity>
                {/* Apple Sign-In Button */}
                <AppleSignin
                  authOptions={{
                    // clientId: "com.mexa.pwa", // Your registered Apple Services
                    clientId: "com.mexxzia.pwa",
                    scope: "email name",
                    redirectURI: "https://app.getmeeza.com/",
                    // redirectURI: "https://getmeeza.com/prollix/appleAuth/appleAuth", // Must match Apple Developer Portal
                    usePopup: true, // Prevents page reload so cart state remains intact
                  }}
                  onSuccess={handleAppleSuccess}
                  onError={handleAppleFailure}
                  render={(props) => (
                    <TouchableOpacity
                      style={styles.appleBtn}
                      activeOpacity={0.85}
                      onPress={props.onClick}
                      disabled={isLoadingAction}
                    >
                      <Ionicons name="logo-apple" size={dynamicSize(20)} color="#FFFFFF" />
                      <Text style={styles.appleBtnText}>
                        {isLoadingAction ? "Connecting..." : "Continue with Apple"}
                      </Text>
                    </TouchableOpacity>
                  )}
                />
              </View>
            </View>
          ) : (
            /* PROCEED BUTTON (Only shown when already logged in) */
            <TouchableOpacity
              style={[styles.proceedBtn, (!canProceed || isLoadingAction) && styles.disabledBtn]}
              onPress={handleProceed}
              disabled={!canProceed || isLoadingAction}
            >
              {isLoadingAction ? (
                <ActivityIndicator color="white" />
              ) : (
                <>
                  <Text style={styles.proceedBtnText}>Join Table Now</Text>
                  <MaterialCommunityIcons name="arrow-right" size={dynamicSize(20)} color="white" />
                </>
              )}
            </TouchableOpacity>
          )}
        </ScrollView>
      </KeyboardAvoidingView>

      {/* WHATSAPP VERIFICATION (Only shown for unauthenticated users) */}
      {/* {!isLoggedIn && (
            <View style={styles.section}>
              <Text style={styles.sectionLabel}>2. ENTER WHATSAPP NUMBER</Text>
              <View style={styles.inputWrapper}>
                <MaterialCommunityIcons name="whatsapp" size={dynamicSize(24)} color="#25D366" />
                <TextInput
                  style={styles.input}
                  placeholder="WhatsApp Number (e.g., 08031234567)"
                  keyboardType="phone-pad"
                  value={whatsappNumber}
                  onChangeText={setWhatsappNumber}
                  placeholderTextColor="#999"
                  maxLength={15}
                />
              </View>
            </View>
          )} */}

      <Modal visible={isErrorCompVisible} transparent={true} animationType="fade">
        <ErrorAlertComp message={errorMessage} onClose={() => setIsErrorCompVisible(false)} />
      </Modal>
    </SafeAreaView>
  );
};

export default TableInvitationCheckout;

const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: "white" },
  centerLoader: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "white",
  },
  loadingTextText: {
    marginTop: 10,
    fontFamily: "Sen-Regular",
    fontSize: dynamicFontSize(14),
    color: "#555",
  },
  scrollContent: {
    padding: dynamicSpacing(20),
    paddingBottom: dynamicSpacing(100),
    backgroundColor: Colours.LIGHTEST,
  },
  section: { marginBottom: dynamicSpacing(25) },
  sectionLabel: {
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(12),
    color: "#888",
    letterSpacing: 1.2,
    marginBottom: dynamicSpacing(12),
  },
  brandHeroCard: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "white",
    padding: dynamicSpacing(20),
    borderRadius: dynamicSize(16),
    marginBottom: dynamicSpacing(25),
    elevation: 2,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
  },
  brandImage: {
    width: dynamicSize(70),
    height: dynamicSize(70),
    borderRadius: dynamicSize(14),
    backgroundColor: "#F5F5F7",
  },
  brandMetaInfo: {
    flex: 1,
    marginLeft: dynamicSpacing(15),
    justifyContent: "center",
  },
  nameTxt: {
    color: Colours.DARKGRAY,
    fontSize: dynamicFontSize(18),
    fontFamily: "Sen-Bold",
    marginBottom: dynamicSpacing(4),
  },
  emailTxt: {
    color: Colours.DARKLIGHTGRAY,
    fontSize: dynamicFontSize(13),
    fontFamily: "Sen-Regular",
    lineHeight: dynamicFontSize(18),
  },
  invitationCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: dynamicSize(16),
    padding: dynamicSpacing(20),
    borderWidth: 1,
    borderColor: "#EAEAEA",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.03,
    shadowRadius: 8,
    elevation: 2,
  },
  badgeRow: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#FFF9F2",
    alignSelf: "flex-start",
    paddingHorizontal: dynamicSpacing(10),
    paddingVertical: dynamicSpacing(4),
    borderRadius: dynamicSize(20),
    marginBottom: dynamicSpacing(12),
    borderWidth: 0.5,
    borderColor: "#F3D9BA",
  },
  invitationBadgeText: {
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(11),
    color: "#C58940",
    marginLeft: dynamicSpacing(5),
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  styledTableTitle: {
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(22),
    color: Colours.DARKGRAY,
    letterSpacing: -0.3,
  },
  inputWrapper: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "white",
    paddingHorizontal: dynamicSpacing(15),
    borderRadius: dynamicSize(12),
    borderWidth: 1,
    borderColor: "#DDD",
    height: dynamicSize(56),
    marginBottom: dynamicSpacing(10),
  },
  input: {
    flex: 1,
    marginLeft: dynamicSpacing(10),
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(15),
    color: Colours.DARKGRAY,
  },
  /* Social Auth Button Styles */
  authBtnContainer: {
    gap: dynamicSpacing(12),
  },
  appleBtn: {
    backgroundColor: "#000000",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    height: dynamicSize(54),
    borderRadius: dynamicSize(14),
    gap: dynamicSpacing(10),
    elevation: 1,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  appleBtnText: {
    color: "#FFFFFF",
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(15),
  },
  googleBtn: {
    backgroundColor: "#FFFFFF",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    height: dynamicSize(54),
    borderRadius: dynamicSize(14),
    borderWidth: 1,
    borderColor: "#E2E8F0",
    gap: dynamicSpacing(10),
    elevation: 1,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
  },
  googleBtnText: {
    color: "#1E293B",
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(15),
  },

  proceedBtn: {
    backgroundColor: Colours.PRIMARY,
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: dynamicSpacing(18),
    borderRadius: dynamicSize(15),
    gap: dynamicSpacing(10),
    marginTop: dynamicSpacing(10),
  },
  disabledBtn: { backgroundColor: "#CCC" },
  proceedBtnText: { color: "white", fontFamily: "Sen-Bold", fontSize: dynamicFontSize(16) },
});
